// PrebuiltsWidget — surfaces curated prebuilt-dashboard tiles on the Home
// page. Backend (`/home/widget/prebuilts`) returns ≤6 items, with NEW or
// recently-updated ones first. Click a tile → navigate to Gallery filtered
// to that template. Visible to every role (iter-89 — user request).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, Plus, ArrowUpRight } from "lucide-react";

export default function PrebuiltsWidget({ onEmpty }) {
    const [items, setItems] = useState(null);
    const navigate = useNavigate();
    useEffect(() => {
        api.get("/home/widget/prebuilts")
            .then((r) => {
                const list = r.data || [];
                setItems(list);
                if (!list.length && onEmpty) onEmpty();
            })
            .catch(() => { setItems([]); if (onEmpty) onEmpty(); });
    }, [onEmpty]);

    if (items === null) {
        return (
            <div className="border border-border rounded-sm bg-card p-4 h-full" data-testid="home-widget-prebuilts-loading">
                <div className="eyebrow text-[10px] mb-3">Prebuilt templates</div>
                <div className="text-xs text-muted-foreground">Loading…</div>
            </div>
        );
    }

    return (
        <div className="border border-border rounded-sm bg-card p-4 h-full overflow-hidden flex flex-col" data-testid="home-widget-prebuilts">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
                    <span className="eyebrow text-[10px]">Prebuilt templates</span>
                </div>
                <button
                    onClick={() => navigate("/gallery?kind=prebuilt_dashboard")}
                    className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-primary"
                    data-testid="home-prebuilts-all">
                    Browse all →
                </button>
            </div>
            {items.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">
                    No templates available yet.
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 overflow-y-auto scrollbar-thin">
                    {items.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => navigate(`/gallery?focus=${t.id}`)}
                            className="border border-border rounded-sm p-3 text-left hover:border-primary/60 transition-colors group"
                            data-testid={`home-prebuilt-${t.id}`}
                        >
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="text-sm font-medium leading-tight line-clamp-2 flex-1">{t.name}</div>
                                {t.is_new && (
                                    <span className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 shrink-0">
                                        New
                                    </span>
                                )}
                            </div>
                            <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-2">
                                <span>{t.category || "general"}</span>
                                {t.version && <span>· v{t.version}</span>}
                                <ArrowUpRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100" />
                            </div>
                        </button>
                    ))}
                </div>
            )}
            <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                <span>{items.length} of {Math.max(items.length, 6)}</span>
                <button onClick={() => navigate("/gallery")} className="hover:text-primary flex items-center gap-1" data-testid="home-prebuilts-add">
                    <Plus className="h-3 w-3" /> Use one
                </button>
            </div>
        </div>
    );
}
